import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
} from "@dnd-kit/core";
import { arrayMove, rectSortingStrategy, SortableContext, sortableKeyboardCoordinates } from "@dnd-kit/sortable";

import { cn } from "../../lib/utils";
import { sortableListColumnClassName, sortableListColumnStyle, type SortableListColumns } from "./columnStyles";
import { SortableListItem } from "./SortableListItem";

export type { SortableListColumns, SortableListSpan } from "./columnStyles";
export type { SortableListItemProps, SortableListItemRenderProps } from "./SortableListItem";

// Small enough that the handle feels immediate, large enough that a click on content inside an
// item isn't swallowed as a drag.
const POINTER_ACTIVATION_DISTANCE_PX = 4;

export interface SortableListProps {
  /**
   * Ordered item ids — the single source of truth for order. The list keeps no internal order
   * state, so a consumer that doesn't feed `onReorder` back into `items` will see no reordering.
   */
  items: string[];
  /** Called once when a drag ends, with the full reordered id list. */
  onReorder: (orderedIds: string[]) => void;
  /**
   * Columns in the grid, as a count or a per-breakpoint object.
   * @default 1
   */
  columns?: SortableListColumns;
  /**
   * Tailwind gap class for the grid.
   * @default "gap-3"
   */
  gap?: string;
  className?: string;
  /** One `SortableList.Item` per id in `items`. */
  children: ReactNode;
}

/**
 * A drag-to-reorder grid, sortable by pointer and by keyboard, with screen-reader announcements.
 * Built on dnd-kit.
 *
 * It holds no opinion on row height, padding, border, or background — only column layout and
 * ordering. Pass any of that through `className` on the list or on an individual `Item`.
 *
 * Order is fully controlled: `items` is the source of truth and `onReorder` fires once per drag
 * with the new order, which the consumer stores and feeds back.
 *
 * @example
 * ```tsx
 * const [ids, setIds] = useState(["a", "b", "c"]);
 *
 * <SortableList items={ids} onReorder={setIds} columns={{ base: 1, md: 2 }}>
 *   {ids.map((id) => (
 *     <SortableList.Item key={id} id={id} className="h-[240px]" span={expanded[id] ? "full" : 1}>
 *       {({ handleProps, isDragging }) => (
 *         <AnalyticsCard title={titles[id]}>
 *           <AnalyticsCard.Value>{values[id]}</AnalyticsCard.Value>
 *           <AnalyticsCard.Toolbar dragHandleProps={handleProps} dragging={isDragging} />
 *         </AnalyticsCard>
 *       )}
 *     </SortableList.Item>
 *   ))}
 * </SortableList>
 * ```
 */
const SortableListRoot = ({ items, onReorder, columns = 1, gap = "gap-3", className, children }: SortableListProps) => {
  const { t } = useTranslation();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: POINTER_ACTIVATION_DISTANCE_PX } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const announcements: Announcements = {
    onDragStart: ({ active }) => t("Picked up item {{id}}.", { id: String(active.id) }),
    // dnd-kit fires onDragOver on pickup with `over` equal to the dragged item; announcing that
    // would immediately overwrite the "picked up" message with a move that never happened.
    onDragOver: ({ active, over }) =>
      over && over.id !== active.id
        ? t("Item {{id}} was moved over position {{position}}.", { id: String(active.id), position: String(over.id) })
        : undefined,
    onDragEnd: ({ active, over }) =>
      over
        ? t("Item {{id}} was dropped at position {{position}}.", { id: String(active.id), position: String(over.id) })
        : t("Item {{id}} was dropped.", { id: String(active.id) }),
    onDragCancel: ({ active }) =>
      t("Reordering was cancelled. Item {{id}} returned to its original position.", { id: String(active.id) }),
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const from = items.indexOf(String(active.id));
    const to = items.indexOf(String(over.id));
    if (from === -1 || to === -1) return;
    onReorder(arrayMove(items, from, to));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      accessibility={{ announcements }}
    >
      <SortableContext items={items} strategy={rectSortingStrategy}>
        <div
          data-slot="sortable-list"
          className={cn("grid", sortableListColumnClassName, gap, className)}
          style={sortableListColumnStyle(columns)}
        >
          {children}
        </div>
      </SortableContext>
    </DndContext>
  );
};

export const SortableList = Object.assign(SortableListRoot, { Item: SortableListItem });
